/**
 * SkillDelivery Platform
 *
 * Copyright © 2026 SKD RATWANI Technology & Software Solutions Pvt Ltd
 * All Rights Reserved.
 *
 * useConnectAccess -- R13 web Round 1.
 *
 * Returns { loading, hasAccess, user } for the Connect feature gate.
 * Server-side `requireConnectAccess` is still authoritative; this
 * hook is purely for hide/show on the owner UI.
 *
 * Shape:
 *   - On first mount, hits `GET /api/users/me`, reads
 *     `data.features.connect` + caches to `localStorage.sd.features`
 *     so a page refresh doesn't flash the Connect entry out of the
 *     menu.
 *   - Falls back to the cached value when the fetch fails (network).
 *   - Null when logged out.
 */

import { useEffect, useState } from 'react';
import api from '../services/api';

const CACHE_KEY = 'sd.features';
const CACHE_MAX_AGE_MS = 60 * 60 * 1000; // 1 hour

function _readCache() {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !parsed.at || Date.now() - parsed.at > CACHE_MAX_AGE_MS) return null;
    return parsed;
  } catch (_) { return null; }
}

function _writeCache(features, user) {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify({
      at: Date.now(),
      features: features || {},
      user: user ? {
        id: user._id || user.id || null,
        fullName: user.fullName || null,
        email: user.email || null,
        connectHandle: user.connectHandle || null
      } : null
    }));
  } catch (_) { /* best-effort */ }
}

export default function useConnectAccess() {
  const [state, setState] = useState(() => {
    const token = typeof localStorage !== 'undefined' ? localStorage.getItem('token') : null;
    if (!token) return { loading: false, hasAccess: false, user: null };
    const cached = _readCache();
    if (cached) {
      return {
        loading: false,
        hasAccess: !!(cached.features && cached.features.connect),
        user: cached.user || null
      };
    }
    return { loading: true, hasAccess: false, user: null };
  });

  useEffect(() => {
    const token = typeof localStorage !== 'undefined' ? localStorage.getItem('token') : null;
    if (!token) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/users/me');
        if (cancelled) return;
        // api.get returns the raw response body: { success, data: user }.
        const data = (res && res.data) || null;
        const features = (data && data.features) || {};
        const user = data || null;
        _writeCache(features, user);
        setState({
          loading: false,
          hasAccess: !!features.connect,
          user: user ? {
            id: user._id || user.id || null,
            fullName: user.fullName || null,
            email: user.email || null,
            connectHandle: user.connectHandle || null
          } : null
        });
      } catch (err) {
        // 401 from /users/me means the token is dead (expired/invalid).
        // Scope the hard redirect to /connect/* only: the hook is also
        // called from Sidebar on every authed page, so an unconditional
        // redirect would (a) hijack other pages that already handle
        // their own 401 and (b) risk a loop if /login ever rendered a
        // component that mounts Sidebar. On non-Connect pages we just
        // treat 401 the same as "no Connect access" so the sidebar
        // entry is hidden; the page the user is actually on handles
        // its own auth.
        if (cancelled) return;
        const status = err && err.response && err.response.status;
        const onConnectPage = typeof window !== 'undefined'
          && window.location.pathname.startsWith('/connect');
        if (status === 401 && onConnectPage) {
          const returnTo = encodeURIComponent(
            window.location.pathname + window.location.search
          );
          window.location.href = `/login?returnTo=${returnTo}`;
          return;
        }
        if (status === 401) {
          // Off a Connect page -- hide the sidebar link; let the host
          // page's own auth flow handle the dead token.
          setState({ loading: false, hasAccess: false, user: null });
          return;
        }
        // 403 or network: keep whatever was in cache. Server-side gating
        // still blocks real calls -- this is purely UI hide/show.
        setState((s) => ({ ...s, loading: false }));
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return state;
}
